import { useEffect } from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';
import InitAPI from '../api';
import { errorMessage } from '../../messages';
import { appConfig } from '../../../config/appConfig';
import { getActiveRoles, setActiveRoles, UserRoleType } from '../../../config/roles';

// Ported verbatim from the old init reducer: intersect the JWT authorities with the
// active roles persisted in localStorage so `user.authorities` reflects the current
// role selection while `user.allAuthorities` keeps the full JWT set. Backend
// authorization is unaffected (it only trusts the JWT).
export function applyActiveRoleFilter(
  user: any,
  requestedRoles = getActiveRoles(),
  persistFallback = true
) {
  if (!user) {
    return user;
  }
  let allAuthorities: UserRoleType[] = [];
  if (Array.isArray(user.allAuthorities)) {
    allAuthorities = user.allAuthorities;
  } else if (Array.isArray(user.authorities)) {
    allAuthorities = user.authorities;
  }
  if (!appConfig.activeRolesFeature.active || allAuthorities.length === 0) {
    return { ...user, allAuthorities, authorities: allAuthorities };
  }
  const valid = requestedRoles.filter(role => allAuthorities.includes(role));
  if (valid.length > 0) {
    return { ...user, allAuthorities, authorities: valid };
  }
  if (persistFallback) {
    setActiveRoles(allAuthorities);
  }
  return { ...user, allAuthorities, authorities: allAuthorities };
}

export function applyActiveRolesToUserCache(queryClient: QueryClient, roles: UserRoleType[]) {
  queryClient.setQueryData(['user'], user => applyActiveRoleFilter(user, roles, false));
}

// The authenticated user is platform-independent (same CERN identity on PROD and TEST)
// and the old Redux boot fetched it exactly once via START, never re-fetching on
// platform switch. We keep that contract: a single, permanently-fresh ['user'] query
// with no platform in the key and no retries, so a backend outage surfaces the
// BackendUnreachable screen immediately, exactly like the old single-shot GET_USER.
function useUserQuery() {
  const query = useQuery({
    queryKey: ['user'],
    queryFn: () => InitAPI.getUser().then(res => applyActiveRoleFilter(res.data)),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (query.error) {
      errorMessage({ title: 'Error getting user info', error: query.error });
    }
  }, [query.error]);

  return query;
}

export default useUserQuery;
